import { useEffect, useState } from "react";
import { caseTransformingAxios } from "../v2/caseTransformingAxios";
import type { AiSuggestions, Message } from "./types";

type Options = {
  aiAssistantContactUuid?: string;
  conversationOwnerPersonUuids?: string[];
  disabled?: boolean;
  messages?: Message[];
};

const useSmsAiSuggestions = ({ aiAssistantContactUuid, conversationOwnerPersonUuids, disabled, messages }: Options) => {
  const ownersKey = conversationOwnerPersonUuids?.join(",") || "";
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AiSuggestions | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    setSuggestions(null);
    if (disabled || !messages?.length) return;
    setLoading(true);
    const owners = ownersKey ? ownersKey.split(",") : null;
    const isAgent = (uuid: string) => (owners ? owners.includes(uuid) : uuid !== aiAssistantContactUuid);
    const formattedMessages = messages
      .filter((m) => m.message.trim() !== "")
      .map((m) => ({
        sender: isAgent(m.createdByUserPersonUuid) ? "agent" : "lead",
        text: m.message,
        timestamp: m.dateCreated,
      }));
    caseTransformingAxios
      .post<AiSuggestions>(
        "/brivity_ai/sms/reply-suggestions",
        { leadPersonUuid: aiAssistantContactUuid, messages: formattedMessages },
        { signal: abortController.signal },
      )
      .then((response) => {
        setSuggestions(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch AI suggestions:", error);
        setLoading(false);
      });
    return () => {
      abortController.abort();
    };
  }, [aiAssistantContactUuid, ownersKey, disabled, messages]);

  return { loading, suggestions };
};

export default useSmsAiSuggestions;
